import React, { useContext } from 'react';
import { isSchemaObject, OpenAPIObject, SchemaObject } from 'openapi3-ts';
import Context from '../../../contexts/docs';
import { BaseProps, SchemaObjectEntry } from './index';
import { replaceApiRouteLinks, snarkdownWithParagraphs } from '../utilities';

import SchemaLayout from './SchemaLayout/SchemaLayout';
import snarkdown from 'snarkdown';
import styles from './index.module.scss';
import CodeBlockV2 from '../CodeBlockV2';
import { normalizeSchemaForRendering } from './SchemaLayout/schemaUtils';

interface ModelSchemaInternalProps extends BaseProps {
  definition: OpenAPIObject;
  modelName: string;
  simplifyForBill: boolean;
}

const generateModelSchema = (modelName: string, definition: OpenAPIObject) => {
  if (modelName == null) {
    throw new Error(
      "Schema: props.modelName must be set if you're using a model type",
    );
  }
  const model: SchemaObject = definition.components.schemas[modelName];
  if (model == null) {
    // eslint-disable-next-line no-console
    console.warn(`Schema: model ${modelName} not found in API definition.`);
    return null;
  }
  if (!isSchemaObject(model)) {
    throw new Error('Schema: model was not an SchemaObject');
  }

  return normalizeSchemaForRendering(model);
};

// The single example object this model renders, from whichever of the three
// sources it declares, or undefined when it declares none. Returning one object
// (rather than each source's own wrapper shape) lets the rendered string and the
// line numbers SchemaRow computes derive from the same value.
const resolveExampleObject = (
  model: SchemaObject,
  entries: Array<SchemaObjectEntry>,
  exampleIndex: number = 0,
): unknown => {
  const declared =
    model['x-examples'] ??
    (model['example'] != null ? { 'example-1': model['example'] } : null);

  if (declared != null) {
    const candidates = Object.values(declared);
    // An empty `x-examples: {}` (RemovedTransaction) declares no example.
    if (candidates.length === 0) {
      return undefined;
    }
    const chosen = candidates[exampleIndex] ?? candidates[0];
    // Entries are either wrapped as `{ value: ... }` or the example itself.
    return chosen != null &&
      typeof chosen === 'object' &&
      Object.keys(chosen).length === 1 &&
      'value' in chosen
      ? (chosen as { value: unknown }).value
      : chosen;
  }

  // in exchange docs, we removed the x-examples from some of the base models (BaseAccount,
  // BaseIdentity, BaseTransaction, etc) because they were overriding
  // the x-examples in routes that were using these base models in allOf (e.g. LoanAccount).
  // Instead of using x-examples in the base routes we will use in-line examples.
  const exampleObject = {};
  entries.forEach(([key, property]) => {
    // Properties without an example are dropped by JSON.stringify, so omitting
    // them here keeps this object aligned with the rendered JSON.
    if (property.example !== undefined) {
      exampleObject[key] = property.example;
    }
  });
  return exampleObject;
};

const generateExampleResponse = (
  model: SchemaObject,
  entries: Array<SchemaObjectEntry>,
  exampleIndex: number,
) => {
  const exampleObject = resolveExampleObject(model, entries, exampleIndex);
  if (exampleObject === undefined) {
    return { exampleResponse: '', maybeExamples: null };
  }
  // Wrapped in `value` because SchemaRow reads `examples[key].value`. Typed
  // loosely to match SchemaObject['examples'], which openapi3-ts declares as an
  // array even though callers pass a keyed object.
  const maybeExamples: SchemaObject['examples'] = {
    'example-1': { value: exampleObject },
  } as unknown as SchemaObject['examples'];
  return {
    exampleResponse: JSON.stringify(exampleObject, null, 2),
    maybeExamples,
  };
};

/**
 * ModelSchema! Used for webhooks, but also standalone model schemas (for
 * example, the AccountType schema or CurrencyCode schema at the bottom of the
 * accounts page). The core exchange team also uses this for a lot of their
 * standalone model schemas (like all of the "Data Definition"s).
 */

const ModelSchema: React.FC<ModelSchemaInternalProps> = (props) => {
  const { selectedCode, dispatch } = useContext(Context);
  const [validLineNumbers, setValidLineNumbers] = React.useState<Set<number>>(
    new Set(),
  );

  // Rows report which code line they map to, so clicking a line can highlight
  // back. Mirrors ResponseSchema.
  const handleSchemaRowLineNumbers = React.useCallback(
    (lineStart: number | null) => {
      if (lineStart !== null) {
        setValidLineNumbers((prev) => new Set(prev).add(lineStart));
      }
    },
    [],
  );

  const handleLineClick = (lineNumber: number) => {
    dispatch({
      type: 'SET_SELECTED_CODE',
      payload: {
        route: props.modelName,
        item: '__CODE_CLICK__',
        startLine: lineNumber - 1,
        endLine: lineNumber - 1,
      },
    });
  };

  const modelSchema = generateModelSchema(props.modelName, props.definition);
  if (modelSchema == null) {
    return null;
  }
  const entries: Array<SchemaObjectEntry> = Object.entries(
    modelSchema.properties,
  );
  const { exampleResponse, maybeExamples } = generateExampleResponse(
    modelSchema,
    entries,
    props.exampleIndex,
  );

  // Model schemas have no route, so the model name is the selection namespace.
  const showLineStart =
    selectedCode.route === props.modelName &&
    selectedCode.item !== '__CODE_CLICK__'
      ? selectedCode.startLine
      : null;
  const showLineEnd =
    selectedCode.route === props.modelName &&
    selectedCode.item !== '__CODE_CLICK__'
      ? selectedCode.endLine
      : null;
  return (
    <SchemaLayout
      route={props.modelName}
      schemaName={props.modelName}
      responseExamples={maybeExamples}
      stringCode={exampleResponse}
      onSchemaRowLineNumbers={handleSchemaRowLineNumbers}
      simplify={props.simplifyForBill}
      tableOnly={props.tableOnly}
      expandAll={!props.collapse}
      expandText={props.expandText}
      hideText={props.hideText}
      unhideFlag={props.unhideFlag}
      entries={entries}
      schema={modelSchema}
      type='model'
      exchangeSpec={props.exchangeSpec}
      coreExchangeSpec={props.coreExchangeSpec}
      required={modelSchema.required}
      description={
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{
            __html: snarkdownWithParagraphs(
              snarkdown,
              replaceApiRouteLinks(modelSchema.description, props.definition),
            ),
          }}
        />
      }
      headerText='Properties'
    >
      {props.children != null ? (
        <div className={styles.codeBlock}>{props.children}</div>
      ) : (
        <CodeBlockV2
          className={styles.codeBlock}
          code={exampleResponse}
          lang='json'
          title={props.exampleTitle != null ? props.exampleTitle : 'API Object'}
          showLineStart={showLineStart}
          showLineEnd={showLineEnd}
          onLineClick={handleLineClick}
          validLineNumbers={validLineNumbers}
        />
      )}
    </SchemaLayout>
  );
};

export default ModelSchema;
